<template>
  <div class="tw-bg-sf-snow-lighter tw-h-full">
    <div class="tw-shadow-sidepane tw-sticky tw-top-0 tw-bg-sf-snow-lighter">
      <div :class="[baseTitleRowClasses]">
        <h3 class="tw-text-lg" :class="baseTitleClasses">
          {{ I18n.t('user.audit_logs.title') }}
        </h3>

        <div class="tw-ml-auto">
          <SfButtonIcon
            data-testid="drawer-close"
            icon="arrow-right"
            @click="drawerStore.closeDrawer"
          />
        </div>
      </div>
      <div v-if="contextName" :class="[baseTitleRowClasses]">
        <h4 class="tw-text-base" :class="baseTitleClasses">
          {{ contextName }}
        </h4>
      </div>
    </div>
    <div
      v-if="isAnyFetchInProgress"
      class="sf-align-c tw-my-12"
      data-testid="sidepane-loading-spinner"
    >
      <div class="sf-spinner sf-inline-block"></div>
    </div>
    <AuditLogPerDay
      v-if="!isEmpty && !isAnyFetchInProgress"
      :items="auditItems"
    ></AuditLogPerDay>
    <div
      v-if="isEmpty && !isAnyFetchInProgress"
      class="sf-empty-state sf-empty-state-small"
    >
      <img
        :src="$imagesRequire('empty_states/communication.svg')"
        class="sf-empty-state-image"
        width="200"
        height="154"
      />
      <div class="sf-empty-state-title">
        {{ I18n.t('communication.empty_state_audits.title') }}
      </div>
      <div class="sf-c-text">
        {{ I18n.t('communication.empty_state_audits.sidepane_message') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'

import AuditLogPerDay from './audit-log-per-day.vue'
import useAuditLogData from './composables/use-audit-log-data'
import { SfButtonIcon } from '@app/shared/vue/components/sf-button'
import { SilverfinContext } from '@app/shared/types/sf-data'
import useI18n from '@app/shared/vue/composables/use-I18n'

import { useAuditsStore } from './store/audits'
import { useContext } from '@app/shared/pinia/context'
import { useDrawer } from '@app/shared/pinia/drawer'

const I18n = useI18n()

const auditsStore = useAuditsStore()

const { auditItems, isAnyFetchInProgress, unsetHighlightedCard } =
  useAuditLogData()

const isEmpty = computed(() => auditItems.value.length === 0)

const contextStore = useContext()
const { context, contextName } = storeToRefs(contextStore)

unsetHighlightedCard()
window.eventHub.$emit('clearAnchor')

const drawerStore = useDrawer()

const baseTitleRowClasses = [
  'tw-flex',
  'tw-items-center',
  'tw-px-6',
  'tw-py-3',
  'tw-border-solid',
  'tw-border-b',
  'tw-border-x-0',
  'tw-border-t-0',
  'tw-border-sf-neutral-lighter-2',
  'tw-h-[var(--secondary-subheader-height)]',
]
const baseTitleClasses = ['tw-my-0', 'tw-text-sf-dark-dark']

// React to the context changing and fetch the fresh audit logs
watch(
  () => context.value,
  (newValue, oldValue) => {
    const needsFreshData =
      !oldValue ||
      newValue.type !== oldValue.type ||
      newValue.id !== oldValue.id

    const isRelevantContext =
      newValue.type === SilverfinContext.Account ||
      newValue.type === SilverfinContext.ReconciliationText

    if (needsFreshData && isRelevantContext) {
      const newType = newValue.type as
        | SilverfinContext.Account
        | SilverfinContext.ReconciliationText

      auditsStore.fetchAudits({
        contextId: newValue.id,
        contextType: newType,
      })
    }
  },
  { immediate: true }
)
</script>
